/**
 * useViewMetadata.ts - useViewMetadata Hook
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing view-specific metadata
 * 
 * @param {string} viewId - Unique view identifier
 * @param {Array} items - All items/records
 * @param {Function} updateItem - Function to update an item
 * @returns {Object} Metadata utilities
 */
export const useViewMetadata = (viewId, items, updateItem) => {
  
  /**
   * Get metadata for a specific record in this view
   * @param {string} recordId - Record ID
   * @returns {Object} View-specific metadata
   */
  const getMetadata = useCallback((recordId) => {
    const record = items.find(item => item.id === recordId);
    if (!record) return {};
    
    return record.viewMetadata?.[viewId] || {};
  }, [items, viewId]);
  
  /**
   * Set metadata for a specific record in this view
   * @param {string} recordId - Record ID
   * @param {Object} metadata - Metadata to set
   */
  const setMetadata = useCallback((recordId, metadata) => {
    const record = items.find(item => item.id === recordId);
    if (!record) return;
    
    const currentViewMetadata = record.viewMetadata || {};
    const currentMetadata = currentViewMetadata[viewId] || {};
    
    updateItem(recordId, {
      viewMetadata: {
        ...currentViewMetadata,
        [viewId]: {
          ...currentMetadata,
          ...metadata,
          lastModified: new Date().toISOString()
        }
      }
    });
  }, [items, viewId, updateItem]);
  
  /**
   * Update a specific metadata field
   * @param {string} recordId - Record ID
   * @param {string} field - Metadata field name
   * @param {*} value - Value to set
   */
  const updateMetadataField = useCallback((recordId, field, value) => {
    const currentMetadata = getMetadata(recordId);
    setMetadata(recordId, {
      ...currentMetadata,
      [field]: value
    });
  }, [getMetadata, setMetadata]);
  
  /**
   * Get sort order for a record
   * @param {string} recordId - Record ID
   * @returns {number} Sort order (undefined if not set)
   */
  const getSortOrder = useCallback((recordId) => {
    const metadata = getMetadata(recordId);
    return metadata.sortOrder;
  }, [getMetadata]);
  
  /**
   * Set sort order for a record
   * @param {string} recordId - Record ID
   * @param {number} order - Sort order
   */
  const setSortOrder = useCallback((recordId, order) => {
    updateMetadataField(recordId, 'sortOrder', order);
  }, [updateMetadataField]);
  
  /**
   * Get all records sorted by this view's sort order
   * @param {Array} fallbackSort - Fallback sort rules if no sortOrder
   * @returns {Array} Sorted items
   */
  const getSortedItems = useCallback((fallbackSort = []) => {
    return [...items].sort((a, b) => {
      const orderA = getMetadata(a.id).sortOrder;
      const orderB = getMetadata(b.id).sortOrder;
      
      // If both have sort order, use it
      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB;
      }
      
      // If only one has sort order, prioritize it
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
      
      // Fall back to fallback sort rules
      for (const rule of fallbackSort) {
        const valueA = a[rule.field];
        const valueB = b[rule.field];
        
        if (valueA === valueB) continue;
        
        const comparison = valueA < valueB ? -1 : 1;
        return rule.direction === 'desc' ? -comparison : comparison;
      }
      
      // Final fallback: creation time or ID
      const timeA = a.createdAt || a.id;
      const timeB = b.createdAt || b.id;
      return timeA < timeB ? -1 : 1;
    });
  }, [items, getMetadata]);
  
  /**
   * Batch update sort orders for multiple records
   * @param {Array<{id: string, sortOrder: number}>} updates
   */
  const batchUpdateSortOrder = useCallback((updates) => {
    updates.forEach(({ id, sortOrder }) => {
      setSortOrder(id, sortOrder);
    });
  }, [setSortOrder]);
  
  /**
   * Clear all metadata for this view
   * @param {string} recordId - Record ID
   */
  const clearMetadata = useCallback((recordId) => {
    const record = items.find(item => item.id === recordId);
    if (!record) return;
    
    const currentViewMetadata = record.viewMetadata || {};
    const { [viewId]: removed, ...remaining } = currentViewMetadata;
    
    updateItem(recordId, {
      viewMetadata: remaining
    });
  }, [items, viewId, updateItem]);
  
  /**
   * Get statistics about metadata usage
   * @returns {Object} Statistics
   */
  const getMetadataStats = useMemo(() => {
    let withMetadata = 0;
    let withSortOrder = 0;
    let totalMetadataSize = 0;
    
    items.forEach(item => {
      const metadata = item.viewMetadata?.[viewId];
      if (metadata) {
        withMetadata++;
        totalMetadataSize += JSON.stringify(metadata).length;
        
        if (metadata.sortOrder !== undefined) {
          withSortOrder++;
        }
      }
    });
    
    return {
      totalRecords: items.length,
      withMetadata,
      withSortOrder,
      averageMetadataSize: withMetadata > 0 ? Math.round(totalMetadataSize / withMetadata) : 0
    };
  }, [items, viewId]);
  
  return {
    // Get/Set metadata
    getMetadata,
    setMetadata,
    updateMetadataField,
    clearMetadata,
    
    // Sort order utilities
    getSortOrder,
    setSortOrder,
    getSortedItems,
    batchUpdateSortOrder,
    
    // Statistics
    getMetadataStats
  };
};

export default useViewMetadata;
